/**
 * RepositoryDetailPage (epic-6 §5 / GIT-2/GIT-4) — route /repositories/:repoId.
 *
 * Tabs:
 *  - Branches: embeds the existing BranchesPage (same route param)
 *  - Commits: list from git sync + "Sync now" button
 *  - Pull Requests: list w/ state badges, Create dialog, transition PATCH,
 *    reviews + review-conversation links
 *  - Mirror status: branch watermarks (lastSeenSha / lastSyncedAt / index)
 *  - Secrets ref: vault picker + key names stored as gitSecretRef on the repo
 */
import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, GitCommitHorizontal, GitPullRequest, RefreshCw, TrendingUp, KeyRound, Plus, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DataTable } from '@/components/shared/DataTable';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useToast } from '@/components/shared/Toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { type ColumnDef } from '@tanstack/react-table';
import { useGetRepositoryByIdQuery, useGetBranchesQuery, useUpdateRepositoryMutation } from '@/store/api/apiSlice';
import { useGetVaultsQuery } from '@/store/api/secretsApi';
import {
  useGetRepositoryCommitsQuery,
  useSyncRepositoryCommitsMutation,
  useGetRepositoryPullRequestsQuery,
  useCreatePullRequestMutation,
} from '@/store/api/repositoriesApi';
import { BranchesPage } from '@/pages/BranchesPage';
import { PR_STATUS_BADGE, PullRequestDetailDialog } from './PullRequestDetailDialog';
import type { Commit, PullRequest } from '@/types';
import { format, formatDistanceToNow } from 'date-fns';

export { PR_STATUS_BADGE };

export function RepositoryDetailPage() {
  const { repoId } = useParams<{ repoId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const { data: repo } = useGetRepositoryByIdQuery(repoId ?? '', { skip: !repoId });
  const { data: branches } = useGetBranchesQuery(repoId ?? '', { skip: !repoId });

  const branchList = (branches as { _id: string; name: string; gitBranchName: string; lastSeenSha?: string; lastSyncedAt?: string; lastIndexStatus: string; lastIndexedCommitSha?: string; lastIndexedAt?: string; indexerSandboxId?: string }[] | undefined) ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/repositories')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{repo?.name ?? 'Repository'}</h1>
          <p className="text-sm text-muted-foreground font-mono">{repo?.gitUrl}</p>
        </div>
      </div>

      <Tabs defaultValue="branches">
        <TabsList>
          <TabsTrigger value="branches">Branches</TabsTrigger>
          <TabsTrigger value="commits">Commits</TabsTrigger>
          <TabsTrigger value="pull-requests">Pull Requests</TabsTrigger>
          <TabsTrigger value="mirror">Mirror status</TabsTrigger>
          <TabsTrigger value="secrets-ref">Secrets ref</TabsTrigger>
        </TabsList>

        <TabsContent value="branches">
          <BranchesPage />
        </TabsContent>

        <TabsContent value="commits">
          <CommitsTab repoId={repoId!} />
        </TabsContent>

        <TabsContent value="pull-requests">
          <PullRequestsTab repoId={repoId!} />
        </TabsContent>

        <TabsContent value="mirror">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-1.5 text-sm"><TrendingUp className="h-4 w-4" /> Branch watermarks</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {branchList.length === 0 ? (
                <EmptyState title="No branches" description="No mirror data yet" />
              ) : (
                <div className="space-y-2">
                  {branchList.map((b) => (
                    <div key={b._id} className="rounded-md border px-3 py-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-sm font-medium">{b.name}</span>
                        <span className="font-mono text-[11px] text-muted-foreground">
                          head {b.lastSeenSha?.slice(0, 12) ?? '—'}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        <Badge variant="outline" className="text-[10px]">
                          last sync {b.lastSyncedAt ? formatDistanceToNow(new Date(b.lastSyncedAt), { addSuffix: true }) : 'never'}
                        </Badge>
                        <Badge variant="outline" className="text-[10px]">
                          index {b.lastIndexStatus ?? 'none'}
                        </Badge>
                        {b.lastIndexedCommitSha && (
                          <Badge variant="outline" className="text-[10px] font-mono">
                            indexed {b.lastIndexedCommitSha.slice(0, 10)}…
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="secrets-ref">
          <SecretsRefTab repoId={repoId!} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Commits tab ─────────────────────────────────────────────────────────────

function CommitsTab({ repoId }: { repoId: string }) {
  const toast = useToast();
  const { data, isLoading } = useGetRepositoryCommitsQuery({ id: repoId });
  const [sync, { isLoading: isSyncing }] = useSyncRepositoryCommitsMutation();

  const commits: Commit[] = (data as { data?: Commit[] } | undefined)?.data ?? [];

  const columns: ColumnDef<Commit, unknown>[] = [
    {
      accessorKey: 'sha',
      header: 'SHA',
      cell: ({ row }) => <span className="font-mono text-xs">{row.original.sha.slice(0, 10)}</span>,
    },
    {
      accessorKey: 'message',
      header: 'Message',
      cell: ({ row }) => <span className="text-sm">{row.original.message}</span>,
    },
    {
      accessorKey: 'author',
      header: 'Author',
      cell: ({ row }) => (
        <div>
          <p className="text-sm">{row.original.author}</p>
          {row.original.authorEmail && <p className="text-xs text-muted-foreground">{row.original.authorEmail}</p>}
        </div>
      ),
    },
    {
      accessorKey: 'branchName',
      header: 'Branch',
    },
    {
      accessorKey: 'filesChanged',
      header: 'Files',
      cell: ({ row }) => <span className="text-sm">{row.original.filesChanged}</span>,
    },
    {
      accessorKey: 'committedAt',
      header: 'Committed',
      cell: ({ row }) => (
        <span className="text-xs" title={format(new Date(row.original.committedAt), 'PPpp')}>
          {formatDistanceToNow(new Date(row.original.committedAt), { addSuffix: true })}
        </span>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-1.5 text-sm"><GitCommitHorizontal className="h-4 w-4" /> Commits</CardTitle>
        <Button
          size="sm"
          onClick={() =>
            sync({ id: repoId })
              .unwrap()
              .then((r) => toast.success(`Synced — ${r.totalCommitsSynced} new commit(s)`))
              .catch((err) => toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Sync failed'))
          }
          disabled={isSyncing}
        >
          <RefreshCw className={`mr-1 h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
          Sync now
        </Button>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <LoadingSpinner className="py-8" />
        ) : commits.length === 0 ? (
          <EmptyState title="No commits" description='Run "Sync now" to fetch history from the git provider' />
        ) : (
          <DataTable columns={columns} data={commits} />
        )}
      </CardContent>
    </Card>
  );
}

// ─── Pull requests tab ───────────────────────────────────────────────────────

function PullRequestsTab({ repoId }: { repoId: string }) {
  const toast = useToast();
  const { data: prs, isLoading } = useGetRepositoryPullRequestsQuery({ id: repoId });
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<PullRequest | null>(null);
  const [form, setForm] = useState({ title: '', headBranch: '', baseBranch: '', body: '' });

  const [createPR] = useCreatePullRequestMutation();

  const prList = (prs as PullRequest[] | undefined) ?? [];

  const columns: ColumnDef<PullRequest, unknown>[] = [
    {
      accessorKey: 'providerNumber',
      header: '#',
      cell: ({ row }) => <span className="font-mono text-xs">#{row.original.providerNumber}</span>,
    },
    {
      accessorKey: 'title',
      header: 'Title',
      cell: ({ row }) => (
        <div>
          <p className="font-medium text-sm">{row.original.title}</p>
          <p className="text-xs text-muted-foreground">
            {row.original.headBranch} → {row.original.baseBranch}
          </p>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <Badge variant={PR_STATUS_BADGE[row.original.status]} className="text-xs">{row.original.status}</Badge>,
    },
    {
      accessorKey: 'reviewConversationId',
      header: 'Review conv.',
      cell: ({ row }) =>
        row.original.reviewConversationId ? (
          <Link to={`/conversations/${row.original.reviewConversationId}`} className="text-xs text-muted-foreground hover:text-primary">
            open <ExternalLink className="inline h-3 w-3" />
          </Link>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => format(new Date(row.original.createdAt), 'MMM d, yyyy'),
    },
  ];

  const submitCreate = async () => {
    if (!form.title || !form.headBranch || !form.baseBranch) {
      toast.error('Title, head and base branches are required');
      return;
    }
    try {
      await createPR({
        id: repoId,
        body: { title: form.title, headBranch: form.headBranch, baseBranch: form.baseBranch, body: form.body || undefined },
      }).unwrap();
      setCreateOpen(false);
      setForm({ title: '', headBranch: '', baseBranch: '', body: '' });
      toast.success('Pull request created');
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to create PR');
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-1.5 text-sm"><GitPullRequest className="h-4 w-4" /> Pull requests</CardTitle>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 h-4 w-4" /> Create PR
        </Button>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <LoadingSpinner className="py-8" />
        ) : prList.length === 0 ? (
          <EmptyState title="No pull requests" description="Create the first PR for this repository" />
        ) : (
          <DataTable columns={columns} data={prList} onRowClick={setSelected} />
        )}
      </CardContent>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Pull Request</DialogTitle>
            <DialogDescription>Opens a PR against the configured git provider (GIT-4).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Head branch</Label>
                <Input value={form.headBranch} onChange={(e) => setForm({ ...form, headBranch: e.target.value })} placeholder="feature/x" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Base branch</Label>
                <Input value={form.baseBranch} onChange={(e) => setForm({ ...form, baseBranch: e.target.value })} placeholder="main" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Body</Label>
              <textarea
                rows={4}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={submitCreate}>Create PR</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {selected && <PullRequestDetailDialog repoId={repoId} pr={selected} onClose={() => setSelected(null)} />}
    </Card>
  );
}

// ─── Secrets ref tab (GIT-2) ─────────────────────────────────────────────────

function SecretsRefTab({ repoId }: { repoId: string }) {
  const toast = useToast();
  const [updateRepository, { isLoading }] = useUpdateRepositoryMutation();
  const { data: vaults } = useGetVaultsQuery();
  const { data: repo } = useGetRepositoryByIdQuery(repoId);

  const current = (repo as { gitSecretRef?: { vaultId?: string; userKey?: string; tokenKey?: string } } | undefined)?.gitSecretRef;
  const vaultList = (vaults as Array<{ _id: string; name: string }> | undefined) ?? [];

  const [form, setForm] = useState({
    vaultId: current?.vaultId ?? '',
    userKey: current?.userKey ?? '',
    tokenKey: current?.tokenKey ?? '',
  });

  const save = async () => {
    // Skip fetching vaults when repo not yet loaded — the form defaults already applied.
    const body = form.vaultId
      ? {
          gitSecretRef: {
            vaultId: form.vaultId,
            userKey: form.userKey || undefined,
            tokenKey: form.tokenKey || undefined,
          },
        }
      : { gitSecretRef: undefined };
    try {
      await updateRepository({ id: repoId, body }).unwrap();
      toast.success('Secrets ref saved');
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to save secrets ref');
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-1.5 text-sm"><KeyRound className="h-4 w-4" /> Git secrets ref</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-0 max-w-lg">
        <p className="text-xs text-muted-foreground">
          Reference vault-backed credentials (GIT-2): a vault and the entry keys for the git username and token.
          Values are never stored on the repository — only the ref.
        </p>
        <div className="space-y-2">
          <Label className="text-xs">Vault</Label>
          <select
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={form.vaultId}
            onChange={(e) => setForm({ ...form, vaultId: e.target.value })}
          >
            <option value="">— none —</option>
            {vaultList.map((v) => (
              <option key={v._id} value={v._id}>{v.name}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">User key (entry key)</Label>
            <Input className="font-mono text-xs" placeholder="GIT_USER" value={form.userKey} onChange={(e) => setForm({ ...form, userKey: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Token key (entry key)</Label>
            <Input className="font-mono text-xs" placeholder="GIT_TOKEN" value={form.tokenKey} onChange={(e) => setForm({ ...form, tokenKey: e.target.value })} />
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={isLoading}>{isLoading ? 'Saving…' : 'Save secrets ref'}</Button>
        </div>
      </CardContent>
    </Card>
  );
}